import React, { useEffect, useMemo, useState } from "react";
import { Users, Building2, ChevronDown, CheckCircle, AlertTriangle } from "lucide-react";

const StepChooseEmployees = ({
  payrollData = [],
  selectedEmployees = [],
  setSelectedEmployees,
  handleEmployeeSelection,
  setSelectedAll,
  selectedCompanyIds = [], // from formData.companyIds (Step 1) — [] means no company filter applied
  modalStyles,
  currentTheme,
  t,
}) => {
  const [collapsedGroups, setCollapsedGroups] = useState({});

  const toggleGroup = (groupId, e) => {
    e.stopPropagation();
    setCollapsedGroups((prev) => ({ ...prev, [groupId]: !prev[groupId] }));
  };

  // ── Paid employees are filtered out at the data level — not just hidden
  // with CSS — so they can never appear in the grouped lists, be picked up
  // by "select all", or be counted in the selected totals below. ──
  const unpaidPayrollData = useMemo(
    () => payrollData.filter((employee) => employee.status !== "paid"),
    [payrollData],
  );
  const paidCount = payrollData.length - unpaidPayrollData.length;

  // Defensive cleanup: if an employee inside `selectedEmployees` becomes
  // "paid" after the fact (e.g. payrollData refreshes mid-flow), drop them
  // from the selection instead of letting a paid employee linger in state.
  useEffect(() => {
    const paidIds = new Set(
      payrollData.filter((emp) => emp.status === "paid").map((emp) => emp.id),
    );
    if (paidIds.size === 0) return;

    const hasStalePaidSelection = selectedEmployees.some((emp) =>
      paidIds.has(emp.id),
    );
    if (hasStalePaidSelection) {
      setSelectedEmployees((prev) =>
        prev.filter((emp) => !paidIds.has(emp.id)),
      );
    }
  }, [payrollData, selectedEmployees, setSelectedEmployees]);

  // The only selections that should ever drive UI / totals are ones that
  // still exist in the unpaid list. This protects the summary at the
  // bottom even if `selectedEmployees` ever contains a stale paid entry.
  // NOTE: this is intentionally NOT restricted to the selected companies —
  // it tracks the full cross-company selection so totals stay correct even
  // while the visible list below is scoped to the active company filter.
  const validSelectedEmployees = useMemo(
    () =>
      selectedEmployees.filter((emp) =>
        unpaidPayrollData.some((u) => u.id === emp.id),
      ),
    [selectedEmployees, unpaidPayrollData],
  );

  // ── Company scoping ─────────────────────────────────────────────────────
  // If one or more companies were selected in Step 1, only show employees
  // who belong to one of those companies. With zero companies selected
  // (legacy single-company flow, or companies haven't loaded), show everyone.
  const hasCompanyFilter = selectedCompanyIds.length > 0;
  const isMultiCompany = selectedCompanyIds.length > 1;

  const scopedPayrollData = useMemo(() => {
    if (!hasCompanyFilter) return unpaidPayrollData;
    return unpaidPayrollData.filter((emp) =>
      selectedCompanyIds.includes(emp.workingCompany),
    );
  }, [unpaidPayrollData, selectedCompanyIds, hasCompanyFilter]);

  const hiddenByCompanyFilterCount = hasCompanyFilter
    ? unpaidPayrollData.length - scopedPayrollData.length
    : 0;

  // ── Grouping ─────────────────────────────────────────────────────────────
  // Single/no company selected → flat department grouping (original behavior).
  // Multiple companies selected → company groups, each with nested department
  // sub-groups, so employees never get mixed across companies visually.
  // Resolve a display/grouping department name for an employee row.
  // Rows normally carry `department` as a string (mapped from the backend's
  // department relation), but stay defensive: the raw backend shape has
  // department as {id, name}, and legacy rows only had employeeType
  // (since renamed to position).
  const resolveDepartment = (employee) =>
    employee.department?.name ??
    (typeof employee.department === "string" && employee.department
      ? employee.department
      : null) ??
    employee.position ??
    employee.employeeType ??
    "Unknown";

  const groupedByDepartment = useMemo(
    () =>
      scopedPayrollData.reduce((acc, employee) => {
        const dept = resolveDepartment(employee);
        if (!acc[dept]) acc[dept] = [];
        acc[dept].push(employee);
        return acc;
      }, {}),
    [scopedPayrollData],
  );

  const groupedByCompanyThenDepartment = useMemo(() => {
    if (!isMultiCompany) return null;
    return scopedPayrollData.reduce((acc, employee) => {
      const company = employee.workingCompany || "Unknown";
      const dept = resolveDepartment(employee);
      if (!acc[company]) acc[company] = {};
      if (!acc[company][dept]) acc[company][dept] = [];
      acc[company][dept].push(employee);
      return acc;
    }, {});
  }, [scopedPayrollData, isMultiCompany]);

  // ── Generic group selection helpers (work for both department-only and
  // company/department-nested groups, since both just operate on an
  // employee[] for a given group). ──
  const isGroupFullySelected = (employees) =>
    employees.every((emp) =>
      validSelectedEmployees.some((selected) => selected.id === emp.id),
    );

  const isGroupPartiallySelected = (employees) =>
    employees.some((emp) =>
      validSelectedEmployees.some((selected) => selected.id === emp.id),
    ) && !isGroupFullySelected(employees);

  const handleGroupSelection = (employees) => {
    const allSelected = isGroupFullySelected(employees);

    if (allSelected) {
      setSelectedEmployees((prev) =>
        prev.filter(
          (selected) => !employees.some((emp) => emp.id === selected.id),
        ),
      );
    } else {
      setSelectedEmployees((prev) => {
        const newEmployees = employees.filter(
          (emp) => !prev.some((selected) => selected.id === emp.id),
        );
        return [...prev, ...newEmployees];
      });
    }
  };

  // "Select all" reflects the currently SCOPED (company-filtered) list, so
  // toggling it never silently grabs or drops employees from a company that
  // isn't even visible right now.
  const allScopedSelected =
    scopedPayrollData.length > 0 &&
    scopedPayrollData.every((emp) =>
      validSelectedEmployees.some((selected) => selected.id === emp.id),
    );
  const someScopedSelected =
    scopedPayrollData.some((emp) =>
      validSelectedEmployees.some((selected) => selected.id === emp.id),
    ) && !allScopedSelected;

  // Keep the parent's `selectedAll` flag in sync with the scoped view.
  useEffect(() => {
    setSelectedAll(allScopedSelected);
  }, [allScopedSelected, setSelectedAll]);

  const handleSelectAllToggle = () => {
    if (scopedPayrollData.length === 0) return;
    handleGroupSelection(scopedPayrollData);
  };

  // Per-company breakdown for the summary footer when multiple companies
  // are active — makes it obvious how the total splits across companies
  // before processing runs each company's payroll separately.
  const selectedByCompany = useMemo(() => {
    if (!isMultiCompany) return null;
    const map = {};
    validSelectedEmployees.forEach((emp) => {
      const company = emp.workingCompany || "Unknown";
      if (!map[company]) map[company] = { count: 0, total: 0 };
      map[company].count += 1;
      map[company].total += emp.netSalary;
    });
    return map;
  }, [validSelectedEmployees, isMultiCompany]);

  // ── Shared row renderers ────────────────────────────────────────────────
  const renderEmployeeRow = (employee) => {
    const isSelected = validSelectedEmployees.some(
      (emp) => emp.id === employee.id,
    );

    return (
      <div
        key={employee.id}
        style={{
          ...modalStyles.employeeItem,
          paddingLeft: "64px",
          paddingRight: "24px",
          borderRadius: "10px",
          margin: "6px 0",
          background: isSelected
            ? `${currentTheme.colors.success}10`
            : "transparent",
          border: isSelected
            ? `2px solid ${currentTheme.colors.success}40`
            : `1px solid ${currentTheme.colors.borderLight}`,
          boxShadow: isSelected
            ? "0 2px 12px rgba(16,185,129,0.08)"
            : "none",
          transition: "all 0.2s",
        }}
        onClick={() => handleEmployeeSelection(employee)}
        onMouseEnter={(e) => {
          e.currentTarget.style.background = isSelected
            ? `${currentTheme.colors.success}20`
            : `${currentTheme.colors.primary}08`;
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.background = isSelected
            ? `${currentTheme.colors.success}10`
            : "transparent";
        }}
      >
        <input
          type="checkbox"
          checked={isSelected}
          onChange={() => {}}
          style={{
            accentColor: currentTheme.colors.success,
            width: "18px",
            height: "18px",
            marginRight: "12px",
            cursor: "pointer",
          }}
        />

        <div style={{ flex: 1 }}>
          <div
            style={{
              fontWeight: "700",
              color: currentTheme.colors.textPrimary,
              fontSize: "15px",
              marginBottom: "2px",
            }}
          >
            {employee.employeeName}
          </div>
        </div>
        <div style={{ textAlign: "right" }}>
          <div
            style={{
              fontWeight: "700",
              color: currentTheme.colors.success,
              fontSize: "15px",
            }}
          >
            {employee.netSalary.toLocaleString()} {t("currency")}
          </div>
          <div
            style={{
              fontSize: "12px",
              color: currentTheme.colors.textMuted,
            }}
          >
            Net Salary
          </div>
        </div>
      </div>
    );
  };

  const renderDepartmentGroup = (department, employees, { indent = 0 } = {}) => {
    const isFullySelected = isGroupFullySelected(employees);
    const isPartiallySelected = isGroupPartiallySelected(employees);
    const departmentTotal = employees.reduce((sum, emp) => sum + emp.netSalary, 0);

    return (
      <div
        key={department}
        style={{
          marginBottom: indent ? "8px" : "12px",
          marginLeft: indent ? `${indent}px` : 0,
          borderRadius: "8px",
          overflow: "hidden",
          border: `1px solid ${currentTheme.colors.borderLight || "rgba(0,0,0,0.1)"}`,
        }}
      >
        <div
          style={{
            padding: "12px 16px",
            background: `${currentTheme.colors.primary}08`,
            borderBottom: `1px solid ${currentTheme.colors.borderLight || "rgba(0,0,0,0.05)"}`,
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            gap: "12px",
            transition: "background 0.2s",
          }}
          onClick={() => handleGroupSelection(employees)}
          onMouseEnter={(e) =>
            (e.currentTarget.style.background = `${currentTheme.colors.primary}20`)
          }
          onMouseLeave={(e) =>
            (e.currentTarget.style.background = `${currentTheme.colors.primary}10`)
          }
        >
          <input
            type="checkbox"
            checked={isFullySelected}
            onChange={() => {}}
            ref={(el) => {
              if (el) el.indeterminate = isPartiallySelected;
            }}
            style={{
              accentColor: currentTheme.colors.primary,
              width: "20px",
              height: "20px",
              marginRight: "8px",
              cursor: "pointer",
            }}
          />
          <div style={{ flex: 1 }}>
            <div
              style={{
                fontWeight: "800",
                fontSize: "16px",
                color: currentTheme.colors.primary,
                letterSpacing: "0.5px",
              }}
            >
              {department}
            </div>
            <div
              style={{
                fontSize: "13px",
                color: currentTheme.colors.textSecondary,
                marginTop: "2px",
              }}
            >
              <span
                style={{
                  background: currentTheme.colors.primary,
                  color: "#fff",
                  borderRadius: "6px",
                  padding: "2px 8px",
                  fontWeight: "600",
                  fontSize: "12px",
                  marginRight: "8px",
                }}
              >
                {employees.length}
              </span>
              employees • Total:{" "}
              <span
                style={{
                  color: currentTheme.colors.success,
                  fontWeight: "700",
                }}
              >
                {`${departmentTotal.toLocaleString()} ${t("currency")}`}
              </span>
            </div>
          </div>
          <ChevronDown
            size={20}
            style={{
              color: currentTheme.colors.textSecondary,
              opacity: 0.7,
              transform: collapsedGroups[`dept-${department}`] ? "rotate(-90deg)" : "none",
              transition: "transform 0.2s",
              cursor: "pointer",
            }}
            onClick={(e) => toggleGroup(`dept-${department}`, e)}
          />
        </div>

        {!collapsedGroups[`dept-${department}`] && (
          <div
            style={{
              background: currentTheme.colors.cardBackground,
              padding: "0 0 8px 0",
            }}
          >
            {employees.map(renderEmployeeRow)}
          </div>
        )}
      </div>
    );
  };

  const renderCompanyGroup = (company, departmentsMap) => {
    const companyEmployees = Object.values(departmentsMap).flat();
    const isFullySelected = isGroupFullySelected(companyEmployees);
    const isPartiallySelected = isGroupPartiallySelected(companyEmployees);
    const companyTotal = companyEmployees.reduce((sum, emp) => sum + emp.netSalary, 0);

    return (
      <div
        key={company}
        style={{
          marginBottom: "16px",
          borderRadius: "8px",
          overflow: "hidden",
          border: `1px solid ${currentTheme.colors.borderLight || "rgba(0,0,0,0.1)"}`,
        }}
      >
        <div
          style={{
            padding: "14px 16px",
            background: `${currentTheme.colors.primary}12`,
            borderBottom: `1px solid ${currentTheme.colors.borderLight || "rgba(0,0,0,0.05)"}`,
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            gap: "12px",
            position: "sticky",
            top: 0,
            zIndex: 3,
            transition: "background 0.2s",
          }}
          onClick={() => handleGroupSelection(companyEmployees)}
          onMouseEnter={(e) =>
            (e.currentTarget.style.background = `${currentTheme.colors.primary}26`)
          }
          onMouseLeave={(e) =>
            (e.currentTarget.style.background = `${currentTheme.colors.primary}16`)
          }
        >
          <input
            type="checkbox"
            checked={isFullySelected}
            onChange={() => {}}
            ref={(el) => {
              if (el) el.indeterminate = isPartiallySelected;
            }}
            style={{
              accentColor: currentTheme.colors.primary,
              width: "20px",
              height: "20px",
              marginRight: "4px",
              cursor: "pointer",
            }}
          />
          <Building2 size={18} style={{ color: currentTheme.colors.primary, flexShrink: 0 }} />
          <div style={{ flex: 1 }}>
            <div
              style={{
                fontWeight: "800",
                fontSize: "18px",
                color: currentTheme.colors.primary,
                letterSpacing: "0.5px",
              }}
            >
              {company}
            </div>
            <div
              style={{
                fontSize: "13px",
                color: currentTheme.colors.textSecondary,
                marginTop: "2px",
              }}
            >
              <span
                style={{
                  background: currentTheme.colors.primary,
                  color: "#fff",
                  borderRadius: "6px",
                  padding: "2px 8px",
                  fontWeight: "600",
                  fontSize: "12px",
                  marginRight: "8px",
                }}
              >
                {companyEmployees.length}
              </span>
              employees • Total:{" "}
              <span style={{ color: currentTheme.colors.success, fontWeight: "700" }}>
                {`${companyTotal.toLocaleString()} ${t("currency")}`}
              </span>
            </div>
          </div>
          <ChevronDown
            size={22}
            style={{
              color: currentTheme.colors.textSecondary,
              opacity: 0.7,
              transform: collapsedGroups[`comp-${company}`] ? "rotate(-90deg)" : "none",
              transition: "transform 0.2s",
              cursor: "pointer",
            }}
            onClick={(e) => toggleGroup(`comp-${company}`, e)}
          />
        </div>

        {!collapsedGroups[`comp-${company}`] && (
          <div style={{ padding: "14px 14px 6px 14px" }}>
            {Object.entries(departmentsMap).map(([department, employees]) =>
              renderDepartmentGroup(department, employees, { indent: 8 }),
            )}
          </div>
        )}
      </div>
    );
  };

  return (
    <div
      style={{
        ...modalStyles.formSection,
        background: `${currentTheme.colors.primary}05`,
        border: `2px solid ${currentTheme.colors.primary}20`,
        borderRadius: "20px",
        boxShadow: "0 8px 32px rgba(59,130,246,0.08)",
        padding: "32px",
      }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: "24px",
          borderBottom: `1px solid ${currentTheme.colors.borderLight}`,
          paddingBottom: "12px",
        }}
      >
        <h3
          style={{
            ...modalStyles.sectionTitle,
            fontSize: "22px",
            color: currentTheme.colors.textPrimary,
            letterSpacing: "1px",
            textShadow: `0 2px 8px ${currentTheme.colors.primary}10`,
          }}
        >
          <Users size={22} />
          {isMultiCompany ? t("selectByCompany") : t("selectByDepartment")}
        </h3>
        <div
          style={{
            ...modalStyles.checkboxGroup,
            background: `${currentTheme.colors.primary}10`,
            borderRadius: "8px",
            padding: "8px 16px",
            boxShadow: "0 2px 8px rgba(59,130,246,0.06)",
            opacity: scopedPayrollData.length === 0 ? 0.5 : 1,
          }}
        >
          <input
            type="checkbox"
            id="selectAll"
            checked={allScopedSelected}
            ref={(el) => {
              if (el) el.indeterminate = someScopedSelected;
            }}
            onChange={handleSelectAllToggle}
            disabled={scopedPayrollData.length === 0}
            style={{
              accentColor: currentTheme.colors.primary,
              width: "18px",
              height: "18px",
              marginRight: "8px",
              cursor: scopedPayrollData.length === 0 ? "not-allowed" : "pointer",
            }}
          />
          <label
            style={{
              ...modalStyles.label,
              fontWeight: "700",
              color: currentTheme.colors.primary,
            }}
            htmlFor="selectAll"
          >
            {t("selectAll")}{" "}
            <span
              style={{
                background: currentTheme.colors.primary,
                color: "#fff",
                borderRadius: "6px",
                padding: "2px 8px",
                marginLeft: "6px",
                fontSize: "12px",
                fontWeight: "600",
              }}
            >
              {scopedPayrollData.length}
            </span>
          </label>
        </div>
      </div>

      {paidCount > 0 && (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "10px",
            marginBottom: "14px",
            padding: "14px 18px",
            borderRadius: "12px",
            background: `${currentTheme.colors.warning ?? "#f59e0b"}15`,
            border: `1px solid ${currentTheme.colors.warning ?? "#f59e0b"}40`,
            color: currentTheme.colors.textPrimary,
            fontSize: "13.5px",
            lineHeight: 1.5,
          }}
        >
          <AlertTriangle
            size={18}
            style={{ flexShrink: 0, color: currentTheme.colors.warning ?? "#f59e0b" }}
          />
          <span>
            <strong>{paidCount}</strong> employee{paidCount === 1 ? "" : "s"}{" "}
            already marked as paid {paidCount === 1 ? "is" : "are"} hidden
            from this list — paid payrolls can't be reprocessed.
          </span>
        </div>
      )}

      {hiddenByCompanyFilterCount > 0 && (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "10px",
            marginBottom: "20px",
            padding: "14px 18px",
            borderRadius: "12px",
            background: `${currentTheme.colors.primary}10`,
            border: `1px solid ${currentTheme.colors.primary}30`,
            color: currentTheme.colors.textPrimary,
            fontSize: "13.5px",
            lineHeight: 1.5,
          }}
        >
          <Building2 size={18} style={{ flexShrink: 0, color: currentTheme.colors.primary }} />
          <span>
            <strong>{hiddenByCompanyFilterCount}</strong> employee
            {hiddenByCompanyFilterCount === 1 ? "" : "s"} from other companies{" "}
            {hiddenByCompanyFilterCount === 1 ? "is" : "are"} hidden — only employees
            from the {selectedCompanyIds.length === 1 ? "selected company" : "selected companies"}{" "}
            are shown here.
          </span>
        </div>
      )}

      <div
        className="no-scrollbar"
        style={{
          ...modalStyles.employeeList,
          background: currentTheme.colors.cardBackground,
          border: `1px solid ${currentTheme.colors.borderLight || "rgba(0,0,0,0.1)"}`,
          borderRadius: "12px",
          padding: "12px",
        }}
      >
        {scopedPayrollData.length === 0 && (
          <div
            style={{
              padding: "32px 24px",
              textAlign: "center",
              color: currentTheme.colors.textSecondary,
              fontSize: "14px",
            }}
          >
            {hasCompanyFilter
              ? "No unpaid employees found for the selected company/companies."
              : "All employees for this period are already paid."}
          </div>
        )}

        {isMultiCompany
          ? Object.entries(groupedByCompanyThenDepartment).map(([company, departmentsMap]) =>
              renderCompanyGroup(company, departmentsMap),
            )
          : Object.entries(groupedByDepartment).map(([department, employees]) =>
              renderDepartmentGroup(department, employees),
            )}
      </div>

      {validSelectedEmployees.length > 0 && (
        <div
          style={{
            marginTop: "24px",
            padding: "18px",
            background: `${currentTheme.colors.success}20`,
            borderRadius: "12px",
            border: `2px solid ${currentTheme.colors.success}30`,
            boxShadow: "0 2px 12px rgba(16,185,129,0.08)",
            textAlign: "center",
          }}
        >
          <div
            style={{
              color: currentTheme.colors.success,
              fontWeight: "700",
              fontSize: "16px",
              letterSpacing: "0.5px",
            }}
          >
            <CheckCircle
              size={18}
              style={{ marginRight: "8px", verticalAlign: "middle" }}
            />
            {validSelectedEmployees.length} employees selected • Total:{" "}
            <span style={{ color: currentTheme.colors.primary, fontWeight: "800" }}>
              {validSelectedEmployees
                .reduce((sum, emp) => sum + emp.netSalary, 0)
                .toLocaleString()}{" "}
              {t("currency")}
            </span>
          </div>

          {selectedByCompany && (
            <div
              style={{
                marginTop: "12px",
                display: "flex",
                justifyContent: "center",
                flexWrap: "wrap",
                gap: "10px",
              }}
            >
              {Object.entries(selectedByCompany).map(([company, { count, total }]) => (
                <span
                  key={company}
                  style={{
                    fontSize: "12.5px",
                    fontWeight: 600,
                    padding: "4px 10px",
                    borderRadius: "8px",
                    background: `${currentTheme.colors.primary}12`,
                    color: currentTheme.colors.textSecondary,
                  }}
                >
                  {company}: {count} • {total.toLocaleString()} {t("currency")}
                </span>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default StepChooseEmployees;